import { ReactNode, useEffect, useMemo, useState } from 'react';

import { useRegisterParticipant, useShowConsumerCampaignSettingsById } from '@givebacks/api/store';
import { AxiosError, isAxiosError } from 'core';
import { useFormListValidation } from 'core/Hooks';
import { getErrorMessage_v2 } from 'core/Utils';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AdditionalFieldsFormsGuard,
  Alert,
  Box,
  ButtonMantine,
  CurrencyInput,
  Grid,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from 'ui';
import { UseFormReturnType, hasLength, isEmail, useForm } from 'ui/Form';
import { useDebouncedValue } from 'ui/Hooks';
import { IconInfo } from 'ui/Icons';

import { useDonorTranslations } from '../../../Context';
import { useShowCampaignById_new } from '../../../api';
import { useListCampaignPeers } from '../../../api/new/campaigns/peers/get';
import { useCampaignInfoByProvider } from '../../../api/new/campaigns/utils/campaignUtils';
import { CampaignStoreItemTypes } from '../../../api/new/types/utils';

type RegistrationModalProps = {
  opened: boolean;
};
type createParticipant = ReturnType<typeof useRegisterParticipant>['mutate'];

type AdditionalFieldsFormType = {
  options?: {
    name: string;
    required: boolean;
    key: string;
    value: string;
  }[];
};
type RegistrationFormProps = {
  first_name: string;
  last_name: string;
  email: string;
  goal?: number;
  team_id?: string;
  options: Record<string, string | string[]>;
};

type AdditionalFormFieldForm = {
  additional_fields?: UseFormReturnType<AdditionalFieldsFormType>;
};
const RegistrationButton = ({
  form,
  createParticipant,
  setError,
}: {
  form: UseFormReturnType<RegistrationFormProps>;
  createParticipant: createParticipant;
  setError: React.Dispatch<React.SetStateAction<ReactNode | undefined>>;
}) => {
  const { validateForms } = useFormListValidation<AdditionalFormFieldForm>();
  const { causeId, campaignId } = useCampaignInfoByProvider();
  const { data: campaign } = useShowCampaignById_new({ causeId, campaignId });
  const campaignTypeText = campaign?.__type === 'athon' ? 'a-thon' : 'campaign';
  const navigate = useNavigate();
  const handleRegistration = () => {
    validateForms(); // Validate additional fields separately
    if (validateForms().isValid && !form.validate().hasErrors) {
      if (!campaign) return;

      if (!campaign?.cause_id) return;
      createParticipant(
        {
          participant: {
            first_name: form.values.first_name.trim(),
            last_name: form.values.last_name.trim(),
            email: form.values.email.trim(),
            team_id: form.values.team_id,
            options: form.values.options,
            goal: form.values.goal ?? 0,
          },
          campaignId: campaign?.uuid,
          causeId: campaign?.cause_id,
        },
        {
          onSuccess: (data) => {
            navigate(`../participants/${data.uuid}/post-registration`);
          },
          onError(e) {
            const error = e as Error | AxiosError;
            if (
              isAxiosError(error) &&
              error?.response?.data.error.includes('unique') &&
              error.response?.data.attributes.includes('name')
            ) {
              setError(`The name you've entered is already registered for this ${campaignTypeText}. It's possible someone with the same name as yours is registered.
              To complete your registration and ensure donors correctly chose who to donate to, please include additional identifying details with your first name.`);
              form.setErrors({ first_name: true, last_name: true });
            } else {
              setError(getErrorMessage_v2(error));
            }
          },
        }
      );
    }
  };
  return (
    <Modal.AffixedFooter>
      <ButtonMantine fullWidth onClick={handleRegistration}>
        Register
      </ButtonMantine>
    </Modal.AffixedFooter>
  );
};

const RegistrationForm = ({ storeItem }: { storeItem?: CampaignStoreItemTypes }) => {
  const { t } = useDonorTranslations();
  const { data: campaignSettings } = useShowConsumerCampaignSettingsById({
    causeId: storeItem?.cause_id,
    campaignId: storeItem?.uuid,
  });
  const [search, setSearch] = useState('');
  const [debouncedSearch, cancelSearch] = useDebouncedValue(search, 400, { leading: true });
  const { team_uuid } = useParams();
  const { data: teamResponse, isLoading } = useListCampaignPeers({
    campaignId: storeItem?.uuid,
    causeId: storeItem?.cause_id,
    peer_type: 'teams',
    queryOptions: {
      searches: {
        name: { value: debouncedSearch },
      },
    },
  });

  const isAthon = storeItem?.__type === 'athon';
  const isSetGoal = storeItem?.participant_options.goal_setting === 'set_goal';
  const goalType = storeItem?.participant_options.goal_type;

  const [error, setError] = useState<ReactNode | undefined>(undefined);

  const { mutate: createParticipant } = useRegisterParticipant();

  const _options = useMemo(() => {
    if (!storeItem?.participant_options?.registration_options) return {};
    return storeItem?.participant_options?.registration_options?.map((item) => ({
      ...item,
      key: item.name,
    }));
  }, [storeItem?.participant_options?.registration_options]);

  const form = useForm<RegistrationFormProps>({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      goal: undefined,
      team_id: team_uuid,
      options: _options,
    },
    validate: {
      first_name: hasLength({ min: 2 }, 'Must be more than 1 character long'),
      last_name: hasLength({ min: 2 }, 'Must be more than 1 character long'),
      email: isEmail('Invalid email address'),
    },
  });
  const teams = useMemo(() => teamResponse?.teams, [teamResponse?.teams]);
  useEffect(() => {
    return () => {
      cancelSearch();
    };
  }, []);

  return (
    <Stack gap="lg">
      <Text fz="sm">{storeItem?.participant_options.registration_message}</Text>
      <Stack>
        <Group gap={4}>
          <Title fz="md">{storeItem?.participant_options.convention_singular} Information</Title>

          <Box c="cerise.7" fz="sm" fw={500}>
            *
          </Box>
        </Group>
        <Grid gutter={'xs'}>
          <Grid.Col span={6}>
            <TextInput placeholder="First Name" {...form.getInputProps('first_name')} />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput placeholder="Last Name" {...form.getInputProps('last_name')} />
          </Grid.Col>
          <Grid.Col>
            <TextInput
              placeholder="Email"
              {...form.getInputProps('email')}
              rightSection={
                <Tooltip
                  maw={300}
                  label={`This email will be used for communications and managing the ${storeItem?.participant_options.convention_singular.toLowerCase()} page.`}
                >
                  <IconInfo />
                </Tooltip>
              }
            />
          </Grid.Col>

          {isSetGoal && goalType === 'currency' && (
            <Grid.Col>
              <CurrencyInput
                w="100%"
                label={null}
                placeholder={`$    Enter a goal amount `}
                allowNegative={false}
                {...form.getInputProps('goal')}
              />
            </Grid.Col>
          )}
          {isAthon && isSetGoal && goalType === 'units' && (
            <Grid.Col>
              <NumberInput
                allowNegative={false}
                hideControls
                placeholder={`Set a ${storeItem.units_of_activity_singular} goal`}
                w="100%"
                {...form.getInputProps('goal')}
              />
            </Grid.Col>
          )}

          {campaignSettings?.isTeamsEnabled && (
            <Grid.Col>
              <Select
                comboboxProps={{
                  withinPortal: true,
                }}
                placeholder={`Select a ${t('team')}`}
                searchable
                onSearchChange={setSearch}
                searchValue={search}
                data={teams?.map((team) => ({ value: team.uuid, label: team.name })) ?? []}
                rightSection={
                  isLoading || search !== debouncedSearch ? <Loader size="1rem" /> : null
                }
                {...form.getInputProps('team_id')}
              />
            </Grid.Col>
          )}
        </Grid>
      </Stack>
      <AdditionalFieldsFormsGuard
        options={storeItem?.participant_options.registration_options}
        onChange={(_value, formattedValues) =>
          form.setFieldValue('options', formattedValues.asObject)
        }
      />
      <Alert.Collapse type="error" in={error !== undefined}>
        {error}
      </Alert.Collapse>
      <RegistrationButton form={form} createParticipant={createParticipant} setError={setError} />
    </Stack>
  );
};

export const RegistrationModal = ({ opened }: RegistrationModalProps) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(!!opened);
  const { causeId, campaignId } = useCampaignInfoByProvider();
  // Mark: Campaign Update -- skip
  const { data: campaign } = useShowCampaignById_new({ causeId, campaignId });

  return (
    <Modal
      opened={isModalOpen}
      onClose={() => {
        navigate('..');
        setIsModalOpen(false);
      }}
      title={`${
        campaign?.participant_options?.registration_call_to_action ?? 'Register'
      } to Participate`}
    >
      <RegistrationForm storeItem={campaign} />
    </Modal>
  );
};
